import { create } from 'zustand';
import {
  createFilterStore,
  EntityFilter,
  FilterRepresentation,
  FilterStoreState,
  JoinOperator,
} from './filterStore';

const operatorMap = {
  equal: 'eq',
  notequal: 'neq',
  is: 'eq',
  'is not': 'neq',
  'is any of': 'any',
  'is empty': 'eq',
  'is not empty': 'neq',
  lessthan: 'lt',
  lessorequals: 'lte',
  greaterthan: 'gt',
  greaterorequals: 'gte',
  before: 'lte',
  after: 'gte',
  on: 'eq',
  like: 'like',
  ilike: 'ilike',
  list: 'any',
};

const transformToNewFilterRepresentation = (
  filterState
): { filters: FilterRepresentation; joinOperator: string } => {
  /*
    Inventory filters are formatted to drive the ColumnSearchFilter form.
    This transforms that representation into FilterRepresentation.
  */
  const filters = filterState.filters.filter((d) => d.field !== null || d.field === 'joinBy');
  const filterMap: FilterRepresentation = {};
  let joinOperator = 'and';
  if (filters.length > 0 && filterState.logicalOperator !== 'and') {
    joinOperator = filterState.logicalOperator;
  }

  filters.forEach((filter) => {
    const hasEmptyOperator = ['is empty', 'is not empty'].some(
      (e) => e === filter.comparisonOperator
    );

    if (filter.field === null) return;
    if (filter.field === 'joinBy') return;
    if (filter.value === null && !hasEmptyOperator) return;

    if (filter.field in filterMap) {
      if (filter.comparisonOperator === 'is any of' || filter.comparisonOperator === 'list') {
        filterMap[filter.field].push({
          operator: operatorMap[filter.comparisonOperator],
          value: filter.value.join(','),
        });
      } else if (hasEmptyOperator) {
        filterMap[filter.field].push({
          operator: operatorMap[filter.comparisonOperator],
          value: 'null',
        });
      } else {
        filter.value.forEach((val) => {
          filterMap[filter.field].push({
            operator: operatorMap[filter.comparisonOperator],
            value: val,
          });
        });
      }
    } else {
      if (filter.comparisonOperator === 'is any of' || filter.comparisonOperator === 'list') {
        filterMap[filter.field] = [
          {
            operator: operatorMap[filter.comparisonOperator],
            value: filter.value.map((v) => v || 'null').join(','),
          },
        ];
      } else if (hasEmptyOperator) {
        filterMap[filter.field] = [
          {
            operator: operatorMap[filter.comparisonOperator],
            value: 'null',
          },
        ];
      } else {
        filterMap[filter.field] = filter.value.map((val) => {
          return {
            operator: operatorMap[filter.comparisonOperator],
            value: val,
          };
        });
      }
    }
  });

  return { filters: filterMap, joinOperator };
};

export interface OldFilterRepresentation {
  // This is the old representation that drives the <ColumnSearchFilter /> component.
  logicalOperator: string;
  filters: {
    field: string | null;
    comparisonOperator: string | null;
    value: (string | number | Date) | (string | number | Date)[];
    dataType?: string;
  }[];
}

interface InventoryFilter extends EntityFilter {
  setFiltersFromOldRepresentation: (oldFilterMapping: any) => void;
}

export interface PropertyFilterSlice extends FilterStoreState {
  inventory: InventoryFilter;
  inspections: InventoryFilter;
  oldFilterRepresentation: Record<string, OldFilterRepresentation>;
  setOldFilterRepresentation: (entity) => (newState) => void;
}

const setFiltersFromOldRepresentationByEntity = (entity, set) => (oldFilterMapping: any) =>
  set((state) => {
    if (!(entity in state)) return state;
    const newState = { ...state };
    const { filters: newFilters, joinOperator } =
      transformToNewFilterRepresentation(oldFilterMapping);
    newState[entity].filter = newFilters;
    newState[entity].joinOperator = joinOperator;
    return newState;
  });

const initalState = (set, get) => ({
  inventory: {
    filter: {},
    joinOperator: 'and' as JoinOperator, // Defaults to and
    setJoinOperator: (joinOperator: JoinOperator) =>
      set((state) => {
        return {
          inventory: { ...state.inventory, joinOperator },
        };
      }),
    setFiltersFromOldRepresentation: setFiltersFromOldRepresentationByEntity('inventory', set),
    getQueryParamRepresentation: (respectGlobalFilters = true) =>
      get().getQueryParamRepresentationByEntity('inventory', ['properties'])(respectGlobalFilters),
    getBuildDataFilterFunction: () =>
      get().getBuildDataFilterFunctionByEntity('inventory', ['properties']),
  },
  inspections: {
    filter: {},
    joinOperator: 'and' as JoinOperator, // Defaults to and
    setJoinOperator: (joinOperator: JoinOperator) =>
      set((state) => {
        return {
          inspections: { ...state.inspections, joinOperator },
        };
      }),
    setFiltersFromOldRepresentation: setFiltersFromOldRepresentationByEntity('inspections', set),
    getQueryParamRepresentation: (respectGlobalFilters = true) =>
      get().getQueryParamRepresentationByEntity('inspections', ['properties'])(
        respectGlobalFilters
      ),
    getBuildDataFilterFunction: () =>
      get().getBuildDataFilterFunctionByEntity('inspections', ['properties']),
  },
  oldFilterRepresentation: {
    inventory: {
      logicalOperator: 'and',
      filters: [
        {
          field: 'externalId',
          comparisonOperator: 'list',
          value: null,
        },
        {
          field: null,
          comparisonOperator: 'is',
          value: null,
        },
      ],
    },
    inspections: {
      logicalOperator: 'and',
      filters: [
        {
          field: 'status.code',
          comparisonOperator: 'is any of',
          value: ['Pending Approval', 'Pending Approval (Unverified Customer)'],
          dataType: 'string',
        },
        {
          field: null,
          comparisonOperator: 'is',
          value: null,
        },
      ],
    },
  },
  setOldFilterRepresentation:
    (entity: string) => (oldFilterRepresentation: OldFilterRepresentation) => {
      set((state) => ({
        ...state,
        oldFilterRepresentation: {
          ...state.oldFilterRepresentation,
          [`${entity}`]: oldFilterRepresentation,
        },
      }));
    },
});

const useInventoryFilterStore = create<PropertyFilterSlice>((set, get) =>
  createFilterStore<PropertyFilterSlice>(set, get, initalState(set, get))
);

export const useVerificationsFilterStore = create<PropertyFilterSlice>((set, get) =>
  createFilterStore<PropertyFilterSlice>(set, get, initalState(set, get))
);

export default useInventoryFilterStore;
